import React from "react";
import { useLayoutContext } from "../../core/context/layout-context";
import { FINANCES_RECONCILIATION, FINANCES_TRANSACTIONS, FINANCES_ETATS } from "../../core/constants/layout";
import { FinanceStore, reconciliationStats } from "./finance-store";
import { MODULES, CHANNELS, money, todayIso, addDaysIso, fDay, fDateTime } from "./finance-data";
import { FinPage, Kpi, Card, STATUS_TONE, btnGhost } from "./finance-ui";

const BAR = "#b45309"; // single-series bar hue (amber-700) on white

export default function FinanceDashboard({ store }: { store: FinanceStore }) {
  const { handleChildren } = useLayoutContext();
  const { txs } = store;
  const t = todayIso();
  const monthStart = `${t.slice(0, 7)}-01`;
  const ok = txs.filter((x) => x.status === "Réussie");
  const month = ok.filter((x) => x.date.slice(0, 10) >= monthStart);
  const inflow = month.filter((x) => x.amount > 0).reduce((a, x) => a + x.amount, 0);
  const refunds = month.filter((x) => x.type === "Remboursement").reduce((a, x) => a + x.amount, 0);
  const payouts = month.filter((x) => x.type === "Reversement CR").reduce((a, x) => a + x.amount, 0);
  const fees = month.reduce((a, x) => a + x.fee, 0);
  const nIn = month.filter((x) => x.amount > 0).length;
  const pending = txs.filter((x) => x.status === "En attente");
  const failed = txs.filter((x) => x.status === "Échouée" && x.date.slice(0, 10) >= addDaysIso(t, -6));
  const rec = reconciliationStats(store);

  // Daily inflow over the last 14 days.
  const days = Array.from({ length: 14 }, (_, i) => addDaysIso(t, i - 13));
  const daily = days.map((d) => ({ d, v: ok.filter((x) => x.amount > 0 && x.date.startsWith(d)).reduce((a, x) => a + x.amount, 0) }));
  const maxDaily = Math.max(1, ...daily.map((x) => x.v));
  const byModule = MODULES.map((m) => ({ label: m, v: month.filter((x) => x.module === m && x.amount > 0).reduce((a, x) => a + x.amount, 0) }));
  const byChannel = CHANNELS.map((c) => ({ label: c, v: month.filter((x) => x.channel === c && x.amount > 0).reduce((a, x) => a + x.amount, 0) })).sort((a, b) => b.v - a.v);

  return (
    <FinPage
      title="Tableau de bord financier"
      subtitle={`Mois en cours depuis le ${fDay(monthStart)} · toutes agences`}
      actions={
        <>
          <button type="button" onClick={() => handleChildren(FINANCES_ETATS)} className={btnGhost}>
            États & éditions
          </button>
          <button type="button" onClick={() => handleChildren(FINANCES_RECONCILIATION)} className="h-9 px-4 bg-[#92400e] hover:bg-[#78350f] rounded-lg text-xs font-bold text-white">
            Lancer la réconciliation
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        <Kpi label="Encaissé ce mois" value={money(inflow)} sub={`${nIn} paiements reçus`} tone="#047857" />
        <Kpi label="Panier moyen" value={money(nIn ? inflow / nIn : 0)} sub="par encaissement" />
        <Kpi label="Remboursements" value={money(refunds)} sub={`${month.filter((x) => x.type === "Remboursement").length} opérations`} tone="#b91c1c" />
        <Kpi label="Reversements CR" value={money(payouts)} sub="contre-remboursements reversés" tone="#b45309" />
        <Kpi label="Frais opérateurs" value={money(fees)} sub="Mobile Money & banque" tone="#6b5b4b" />
        <Kpi label="Taux de rapprochement" value={`${rec.rate} %`} sub={`${rec.gaps + rec.unidentified} anomalie(s) à traiter`} tone={rec.rate >= 90 ? "#047857" : "#b45309"} onClick={() => handleChildren(FINANCES_RECONCILIATION)} />
      </div>

      <div className="grid xl:grid-cols-[2fr_1fr] gap-4 mb-4">
        <Card title="Encaissements des 14 derniers jours" right={<span className="text-[11px] text-gray-500">max {money(maxDaily)}</span>}>
          <div className="px-4 pb-4">
            <div className="relative h-44 flex items-end gap-[2px] border-b border-[#efe7dc]">
              {[0.5, 1].map((f) => (
                <div key={f} className="absolute left-0 right-0 border-t border-dashed border-[#f3ece2]" style={{ bottom: `${f * 100}%` }} />
              ))}
              {daily.map((x) => (
                <div key={x.d} className="group relative flex-1 h-full flex items-end" title={`${fDay(x.d)} : ${money(x.v)}`}>
                  <div className="w-full rounded-t-[4px] transition-opacity group-hover:opacity-80" style={{ height: `${Math.max(x.v ? 2 : 0, (x.v / maxDaily) * 100)}%`, background: BAR }} />
                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap px-2 py-1 rounded bg-[#1f1b2e] text-white text-[10px] font-semibold z-10">
                    {fDay(x.d)} · {money(x.v)}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-[2px] mt-1.5">
              {daily.map((x, i) => (
                <div key={x.d} className="flex-1 text-center text-[10px] text-gray-500">
                  {i % 2 === 1 || i === 13 ? fDay(x.d) : ""}
                </div>
              ))}
            </div>
          </div>
        </Card>

        <Card title="À traiter">
          <div className="px-4 pb-4 space-y-2 text-xs">
            <TodoRow label="Écarts de montant au rapprochement" n={rec.gaps} onClick={() => handleChildren(FINANCES_RECONCILIATION)} />
            <TodoRow label="Paiements non identifiés (relevés)" n={rec.unidentified} onClick={() => handleChildren(FINANCES_RECONCILIATION)} />
            <TodoRow label="Transactions absentes des relevés" n={rec.missing} onClick={() => handleChildren(FINANCES_RECONCILIATION)} />
            <TodoRow label="Transactions en attente de confirmation" n={pending.length} onClick={() => handleChildren(FINANCES_TRANSACTIONS)} />
            <TodoRow label="Échecs de paiement (7 jours)" n={failed.length} onClick={() => handleChildren(FINANCES_TRANSACTIONS)} />
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Card title="Encaissements du mois par activité">
          <HBars rows={byModule} />
        </Card>
        <Card title="Encaissements du mois par canal">
          <HBars rows={byChannel} />
        </Card>
      </div>

      <Card title="Dernières transactions" right={<button type="button" onClick={() => handleChildren(FINANCES_TRANSACTIONS)} className="text-xs font-semibold text-[#92400e]">Tout voir →</button>}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[720px]">
            <tbody>
              {txs.slice(0, 8).map((x) => (
                <tr key={x.id} className="border-t border-[#f5efe6]">
                  <td className="px-4 py-2.5 font-mono text-[#92400e]">{x.ref}</td>
                  <td className="px-2 py-2.5 text-gray-500 whitespace-nowrap">{fDateTime(x.date)}</td>
                  <td className="px-2 py-2.5">{x.client}</td>
                  <td className="px-2 py-2.5 text-gray-500">{x.module}</td>
                  <td className="px-2 py-2.5 text-gray-500">{x.channel}</td>
                  <td className="px-2 py-2.5">
                    <span className="px-2 py-0.5 rounded font-bold text-[11px]" style={{ background: STATUS_TONE[x.status].bg, color: STATUS_TONE[x.status].fg }}>
                      {x.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold whitespace-nowrap" style={{ color: x.amount < 0 ? "#b91c1c" : "#1f1b2e" }}>
                    {money(x.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </FinPage>
  );
}

function TodoRow({ label, n, onClick }: { label: string; n: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-[#f3ece2] hover:bg-[#fdf8f1] text-left">
      <span className="flex-1">{label}</span>
      <span className={n ? "px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#b45309] font-bold" : "px-2 py-0.5 rounded-full bg-[#f1f5f9] text-gray-500 font-bold"}>{n}</span>
    </button>
  );
}

// Horizontal bars, one series, value labels at the end.
function HBars({ rows }: { rows: { label: string; v: number }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.v));
  const total = rows.reduce((a, r) => a + r.v, 0) || 1;
  return (
    <div className="px-4 pb-4 space-y-2.5">
      {rows.map((r) => (
        <div key={r.label} className="text-xs" title={`${r.label} : ${money(r.v)} (${Math.round((r.v / total) * 100)} %)`}>
          <div className="flex justify-between mb-1">
            <span className="text-gray-700">{r.label}</span>
            <span className="font-semibold">
              {money(r.v)} <span className="text-gray-400 font-normal">· {Math.round((r.v / total) * 100)} %</span>
            </span>
          </div>
          <div className="h-2.5 bg-[#f5efe6] rounded-full overflow-hidden">
            <div className="h-full rounded-r-[4px]" style={{ width: `${(r.v / max) * 100}%`, background: BAR }} />
          </div>
        </div>
      ))}
    </div>
  );
}
